import {
  computeSellingPriceFromProfitAmount,
  computeSellingPriceFromProfitPercent,
} from './budgetEngineV2';
import { computeSimulation } from './priceSimulateCompute';

export const PRICE_SIMULATOR_EXTENSION_KEY = 'price-simulator-3d';

const nf = (v) => {
  const n = typeof v === 'number' ? v : parseFloat(v);
  return Number.isFinite(n) ? n : 0;
};

export function buildSimulationMetadata({ constants, inputProduct, result }) {
  return {
    version: 1,
    applied_at: new Date().toISOString(),
    input: {
      product: inputProduct,
      constants,
    },
    result,
  };
}

function parseExtensionPayload(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === 'object') return raw;
  return null;
}

export function getSavedSimulation(metadata) {
  if (!metadata || typeof metadata !== 'object') {
    return null;
  }
  const raw = parseExtensionPayload(metadata[PRICE_SIMULATOR_EXTENSION_KEY]);
  if (!raw?.input?.product && !raw?.result) {
    return null;
  }
  return raw;
}

export function normalizeVariableCostRow(row, index = 0, { keepEmptyDraft = false } = {}) {
  if (!row || typeof row !== 'object') return null;
  const name = String(row.name ?? row.Name ?? '').trim();
  const costUnit = Number(row.cost_unit ?? row.costUnit ?? row.CostUnit ?? 0);
  const sourceCategoryId = String(
    row.source_category_id ?? row.sourceCategoryId ?? row.SourceCategoryID ?? '',
  ).trim();
  const hasValue = Boolean(name) || (Number.isFinite(costUnit) && costUnit > 0);
  if (!hasValue) {
    if (keepEmptyDraft && row.id) {
      const draft = {
        id: String(row.id),
        name: '',
        cost_unit: 0,
      };
      if (sourceCategoryId) draft.source_category_id = sourceCategoryId;
      return draft;
    }
    return null;
  }
  const normalized = {
    id: String(row.id || row.ID || `vc-${index}-${Date.now()}`),
    name,
    cost_unit: Number.isFinite(costUnit) && costUnit >= 0 ? costUnit : 0,
  };
  if (sourceCategoryId) normalized.source_category_id = sourceCategoryId;
  return normalized;
}

export function normalizeVariableCostRows(rows, options = {}) {
  if (!Array.isArray(rows)) return [];
  return rows.map((row, index) => normalizeVariableCostRow(row, index, options)).filter(Boolean);
}

export function getProductVariableCostDefaults(metadata) {
  if (!metadata || typeof metadata !== 'object') {
    return [];
  }
  const raw = parseExtensionPayload(metadata[PRICE_SIMULATOR_EXTENSION_KEY]);
  if (!raw || typeof raw !== 'object') {
    return [];
  }
  return normalizeVariableCostRows(raw.variable_cost_defaults, { keepEmptyDraft: true });
}

export function getCategoryVariableCostDefaults(metadata) {
  return getProductVariableCostDefaults(metadata);
}

export function mergeVariableCostDefaultLayers(layers) {
  const merged = new Map();
  const order = [];
  const list = Array.isArray(layers) ? layers : [];
  list.forEach((layer) => {
    normalizeVariableCostRows(layer, { keepEmptyDraft: false }).forEach((row) => {
      if (!merged.has(row.id)) {
        order.push(row.id);
      }
      merged.set(row.id, row);
    });
  });
  return order.map((id) => merged.get(id)).filter(Boolean);
}

export function resolveEffectiveVariableCostDefaults({
  inheritedCategoryRows = [],
  productRows = [],
} = {}) {
  return mergeVariableCostDefaultLayers([inheritedCategoryRows, productRows]);
}

export function splitInheritedCategoryVariableCosts(defaults = [], categoryId = '') {
  const normalized = normalizeVariableCostRows(defaults, { keepEmptyDraft: false });
  if (!categoryId) {
    return { inherited: normalized, direct: [] };
  }
  const inherited = [];
  const direct = [];
  normalized.forEach((row) => {
    const sourceId = String(row.source_category_id ?? '').trim();
    if (sourceId && sourceId !== categoryId) {
      inherited.push(row);
    } else {
      direct.push(row);
    }
  });
  return { inherited, direct };
}

export function cloneVariableCostsForSession(rows) {
  return normalizeVariableCostRows(rows, { keepEmptyDraft: false }).map((row, index) => ({
    ...row,
    id: `vc-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 7)}`,
  }));
}

export function buildProductDefaultsMetadataPatch(rows) {
  const variable_cost_defaults = normalizeVariableCostRows(rows, { keepEmptyDraft: true });
  return {
    [PRICE_SIMULATOR_EXTENSION_KEY]: {
      variable_cost_defaults,
    },
  };
}

export function buildCategoryDefaultsMetadataPatch(rows) {
  return buildProductDefaultsMetadataPatch(rows);
}

export function buildMetadataPatch(simulationPayload) {
  return {
    [PRICE_SIMULATOR_EXTENSION_KEY]: simulationPayload,
  };
}

export function variableCostsToSimulationPayload(rows) {
  const normalized = Array.isArray(rows) ? rows : [];
  return normalizeVariableCostRows(normalized, { keepEmptyDraft: false }).map((row) => ({
    name: row.name,
    cost_unit: row.cost_unit,
  }));
}

export function simulationInputToComputePayload(product) {
  if (!product) return null;
  const sellingPriceTotal = resolveSellingPriceTotalFromProduct(product);
  return {
    quantity: Math.max(1, parseInt(product.quantity, 10) || 1),
    material: product.material || 'PLA',
    description: String(product.description ?? '').trim(),
    grams: nf(product.grams),
    print_hours: nf(product.print_hours),
    modeling_hours: nf(product.modeling_hours),
    painting_hours: nf(product.painting_hours),
    variable_costs: variableCostsToSimulationPayload(product.variable_costs || []),
    selling_price_total: sellingPriceTotal,
    selling_price_unit: sellingPriceTotal != null
      ? nf(product.selling_price_unit) || sellingPriceTotal / Math.max(1, parseInt(product.quantity, 10) || 1)
      : null,
  };
}

export function resolveSellingPriceTotalFromProduct(product) {
  if (!product) return null;
  const total = nf(product.selling_price_total);
  if (total > 0) return total;
  const unit = nf(product.selling_price_unit);
  const q = Math.max(1, parseInt(product.quantity, 10) || 1);
  if (unit > 0) return Math.round(unit * q * 100) / 100;
  return null;
}

export function syncSellingPriceUnitFromTotal(sellingPriceTotal, quantity) {
  const q = Math.max(1, parseInt(quantity, 10) || 1);
  const total = nf(sellingPriceTotal);
  if (total <= 0) return clearSellingPriceOverride();
  return {
    selling_price_total: total,
    selling_price_unit: Math.round((total / q) * 100) / 100,
  };
}

export function syncSellingPriceTotalFromUnit(sellingPriceUnit, quantity) {
  const q = Math.max(1, parseInt(quantity, 10) || 1);
  const unit = nf(sellingPriceUnit);
  if (unit <= 0) return clearSellingPriceOverride();
  return {
    selling_price_unit: unit,
    selling_price_total: Math.round(unit * q * 100) / 100,
  };
}

export function clearSellingPriceOverride() {
  return { selling_price_total: null, selling_price_unit: null };
}

export function syncSellingPriceFromProfitPercent(percent, priceAfterReinvest, quantity) {
  const base = nf(priceAfterReinvest);
  const p = Math.max(0, Math.round(nf(percent)));
  if (base <= 0 || p <= 0) return clearSellingPriceOverride();
  const total = computeSellingPriceFromProfitPercent(base, p);
  return syncSellingPriceUnitFromTotal(total, quantity);
}

export function syncSellingPriceFromProfitTotal(amount, priceAfterReinvest, quantity) {
  const base = nf(priceAfterReinvest);
  const profitTotal = nf(amount);
  if (base <= 0 || profitTotal <= 0) return clearSellingPriceOverride();
  const total = computeSellingPriceFromProfitAmount(base, profitTotal);
  return syncSellingPriceUnitFromTotal(total, quantity);
}

export function syncSellingPriceFromProfitUnit(unitAmount, priceAfterReinvest, quantity) {
  const q = Math.max(1, parseInt(quantity, 10) || 1);
  const profitTotal = Math.round(nf(unitAmount) * q * 100) / 100;
  return syncSellingPriceFromProfitTotal(profitTotal, priceAfterReinvest, quantity);
}

export function mergeProductVariableCostsIntoStoredProduct(storedProduct, productVariableCostDefaults) {
  if (!storedProduct) return null;
  return {
    ...storedProduct,
    variable_costs: variableCostsToSimulationPayload(productVariableCostDefaults),
  };
}

export function recomputeSavedSimulationWithProductVariableCosts(
  savedSimulation,
  productVariableCostDefaults,
) {
  if (!savedSimulation?.input?.constants || !savedSimulation?.input?.product) {
    return savedSimulation;
  }

  const mergedProduct = mergeProductVariableCostsIntoStoredProduct(
    savedSimulation.input.product,
    productVariableCostDefaults,
  );
  const payload = simulationInputToComputePayload(mergedProduct);
  if (!payload) return savedSimulation;

  const computed = computeSimulation(savedSimulation.input.constants, payload);
  if (!computed.ok) return savedSimulation;

  return buildSimulationMetadata({
    constants: savedSimulation.input.constants,
    inputProduct: payload,
    result: computed.data,
  });
}

export function getResolvedSavedSimulation(metadata, productVariableCostDefaults = []) {
  const saved = getSavedSimulation(metadata);
  if (!saved) return null;
  return recomputeSavedSimulationWithProductVariableCosts(saved, productVariableCostDefaults);
}

export function applyProductVariableCostsToVariant(variant, productVariableCostDefaults = []) {
  if (!variant) return variant;
  const saved = getSavedSimulation(variant.metadata);
  if (!saved) return variant;

  const nextSim = recomputeSavedSimulationWithProductVariableCosts(saved, productVariableCostDefaults);
  if (!nextSim?.result) return variant;

  const price = nf(nextSim.result?.pricing?.effective_selling_price ?? nextSim.result?.pricing?.final_price);
  const cost = resolveCostForSimulationApply(nextSim, variant.cost ?? 0);
  const metadata = {
    ...(variant.metadata || {}),
    ...buildMetadataPatch(nextSim),
  };

  return { ...variant, price, cost, metadata };
}

export function productFromSavedSimulation(savedSimulation, productVariableCostDefaults = []) {
  const product = savedSimulation?.input?.product;
  if (!product) {
    return null;
  }
  const variable_costs = cloneVariableCostsForSession(productVariableCostDefaults);
  return {
    description: product.description || '',
    quantity: Math.max(1, parseInt(product.quantity, 10) || 1),
    material: product.material || 'PLA',
    grams: product.grams ?? 0,
    print_hours: product.print_hours ?? 0,
    modeling_hours: product.modeling_hours ?? 0,
    painting_hours: product.painting_hours ?? 0,
    variable_costs,
    selling_price_total: nf(product.selling_price_total) > 0 ? nf(product.selling_price_total) : null,
    selling_price_unit: nf(product.selling_price_unit) > 0 ? nf(product.selling_price_unit) : null,
  };
}

export function constantsFromSavedSimulation(savedSimulation) {
  return savedSimulation?.input?.constants || null;
}

export function isSkuCostEmpty(cost) {
  const value = Number(cost);
  return !Number.isFinite(value) || value <= 0;
}

function simulationQuantity(simulation) {
  return Math.max(
    1,
    parseInt(simulation?.input?.product?.quantity, 10) ||
      parseInt(simulation?.result?.detail?.quantity, 10) ||
      1,
  );
}

export function simulatedTotalCostFromSimulation(simulation) {
  if (!simulation?.result) return null;

  const total =
    simulation.result?.composition?.subtotal_before_marketing ??
    simulation.result?.costs?.total_direct_cost;

  if (total == null) return null;

  const numericTotal = Number(total);
  if (!Number.isFinite(numericTotal) || numericTotal < 0) return null;

  return numericTotal;
}

export function simulatedUnitCostFromSimulation(simulation) {
  const total = simulatedTotalCostFromSimulation(simulation);
  if (total == null) return null;

  return total / simulationQuantity(simulation);
}

export function resolveCostFromSimulation(simulation, currentCost = 0) {
  if (!isSkuCostEmpty(currentCost)) {
    return Number(currentCost);
  }

  const fromSimulation = simulatedTotalCostFromSimulation(simulation);
  if (fromSimulation == null || fromSimulation <= 0) {
    return Number(currentCost) || 0;
  }

  return fromSimulation;
}

export function resolveCostForSimulationApply(simulation, fallback = 0) {
  const fromSimulation = simulatedTotalCostFromSimulation(simulation);
  if (fromSimulation != null && fromSimulation >= 0) {
    return fromSimulation;
  }
  return Number(fallback) || 0;
}

export function buildInitialSimulatorProduct({
  constants,
  initialDescription = '',
  initialQuantity = 1,
  initialSellingPriceUnit = null,
  initialSellingPriceTotal = null,
  savedSimulation = null,
  defaultVariableCosts = [],
}) {
  const savedProduct = productFromSavedSimulation(savedSimulation, defaultVariableCosts);
  if (savedProduct) {
    return savedProduct;
  }
  const keys = Object.keys(constants?.materials || {});
  const quantity = Math.max(1, parseInt(initialQuantity, 10) || 1);
  const totalSeed = nf(initialSellingPriceTotal);
  const unitSeed = nf(initialSellingPriceUnit);
  let sellingFields = { selling_price_total: null, selling_price_unit: null };
  if (totalSeed > 0) {
    sellingFields = syncSellingPriceUnitFromTotal(totalSeed, quantity);
  } else if (unitSeed > 0) {
    sellingFields = syncSellingPriceTotalFromUnit(unitSeed, quantity);
  }

  return {
    description: initialDescription || '',
    quantity,
    material: keys.includes('PLA') ? 'PLA' : keys[0] || 'PLA',
    grams: 0,
    print_hours: 0,
    modeling_hours: 0,
    painting_hours: 0,
    variable_costs: cloneVariableCostsForSession(defaultVariableCosts),
    ...sellingFields,
  };
}
